import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { DollarSign, Users, TrendingUp, Copy, LogOut, CheckCircle, BarChart3, Clock, CreditCard, ExternalLink } from 'lucide-react';
import SeoHead from '../components/SeoHead';
import api from '../utils/api';

export default function AffiliateDashboard() {
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('affiliate_token'));
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [stats, setStats] = useState(null);
  const [connectStatus, setConnectStatus] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const affData = JSON.parse(localStorage.getItem('affiliate') || '{}');

  useEffect(() => {
    if (isLoggedIn && affData.code) {
      api.get(`/affiliate/stats/${affData.code}`).then(r => setStats(r.data)).catch(() => {});
      api.get(`/connect/status/${affData.code}`).then(r => setConnectStatus(r.data)).catch(() => {});
    }
  }, [isLoggedIn, affData.code]);

  // Check if returning from Stripe onboarding
  useEffect(() => {
    if (searchParams.get('connect') === 'complete' && affData.code) {
      api.get(`/connect/status/${affData.code}`).then(r => setConnectStatus(r.data)).catch(() => {});
    }
  }, [searchParams, affData.code]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/affiliate/login', loginForm);
      localStorage.setItem('affiliate_token', data.token);
      localStorage.setItem('affiliate', JSON.stringify(data.affiliate));
      setIsLoggedIn(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Login failed');
    } finally { setLoading(false); }
  };

  const handleLogout = () => {
    localStorage.removeItem('affiliate_token');
    localStorage.removeItem('affiliate');
    setIsLoggedIn(false);
    setStats(null);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}?ref=${affData.code}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isLoggedIn) {
    return (
      <>
        <SeoHead title="Affiliate Dashboard — AI Ghostwriter Pro" />
        <section className="min-h-screen flex items-center justify-center px-6 pt-20">
          <div className="w-full max-w-md">
            <h1 className="font-serif text-3xl text-center tracking-tight mb-8">Affiliate <span className="text-primary">Login</span></h1>
            {error && <p className="text-sm text-red-400 text-center mb-4" data-testid="aff-login-error">{error}</p>}
            <form onSubmit={handleLogin} className="space-y-4" data-testid="affiliate-login-form">
              <input type="email" required placeholder="Email" value={loginForm.email}
                onChange={(e) => setLoginForm({...loginForm, email: e.target.value})}
                data-testid="aff-login-email"
                className="w-full px-4 h-12 bg-white/5 border border-white/10 rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary outline-none" />
              <input type="password" required placeholder="Password" value={loginForm.password}
                onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                data-testid="aff-login-password"
                className="w-full px-4 h-12 bg-white/5 border border-white/10 rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary outline-none" />
              <button type="submit" disabled={loading} data-testid="aff-login-submit"
                className="w-full rounded-sm py-3 bg-primary text-primary-foreground font-semibold">
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
            <p className="text-center mt-4 text-sm text-muted-foreground">
              Not an affiliate? <Link to="/affiliate" className="text-primary hover:underline">Join now</Link>
            </p>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <SeoHead title="Affiliate Dashboard — AI Ghostwriter Pro" />
      <section className="pt-32 pb-24 md:pt-40 md:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-serif text-3xl tracking-tight" data-testid="aff-dashboard-title">
                Affiliate <span className="text-primary">Dashboard</span>
              </h1>
              <p className="text-sm text-muted-foreground mt-1">Welcome back, {affData.name || 'Partner'}</p>
            </div>
            <button onClick={handleLogout} data-testid="aff-logout" className="text-muted-foreground hover:text-foreground flex items-center gap-1.5 text-sm">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>

          {/* Referral Link */}
          <div className="bg-white/5 border border-white/10 rounded-sm p-6 mb-8" data-testid="referral-link-section">
            <p className="text-xs uppercase tracking-[0.2em] text-primary mb-3">Your Referral Link</p>
            <div className="flex gap-2">
              <input readOnly value={`${window.location.origin}?ref=${affData.code || ''}`}
                className="flex-1 px-4 h-10 bg-white/5 border border-white/10 rounded-sm text-sm text-foreground" />
              <button onClick={copyLink} data-testid="copy-referral-link"
                className="px-4 h-10 rounded-sm border border-white/20 text-foreground hover:border-primary text-sm flex items-center gap-1.5">
                {copied ? <><CheckCircle className="w-3.5 h-3.5 text-green-400" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Code: <span className="text-primary font-mono">{affData.code}</span></p>
          </div>

          {/* Stripe Connect Section */}
          <div className="bg-white/5 border border-white/10 rounded-sm p-6 mb-8" data-testid="stripe-connect-section">
            <p className="text-xs uppercase tracking-[0.2em] text-primary mb-3">Payout Setup</p>
            {connectStatus?.onboarded ? (
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <div>
                  <p className="text-sm text-green-400 font-semibold">Stripe Connected</p>
                  <p className="text-xs text-muted-foreground">Your payouts will be deposited directly to your bank account via Stripe.</p>
                </div>
              </div>
            ) : (
              <div>
                <p className="text-sm text-muted-foreground mb-3">Connect your Stripe account to receive commission payouts directly to your bank.</p>
                <button
                  onClick={async () => {
                    setConnecting(true);
                    try {
                      const { data } = await api.post(`/connect/onboard/${affData.code}`);
                      if (data.onboarding_url) {
                        window.location.href = data.onboarding_url;
                      } else if (data.status === 'already_onboarded') {
                        setConnectStatus({ onboarded: true });
                      }
                    } catch (e) {
                      setError(e.response?.data?.detail || 'Failed to start Stripe setup');
                    } finally { setConnecting(false); }
                  }}
                  disabled={connecting}
                  data-testid="connect-stripe-btn"
                  className="inline-flex items-center gap-2 rounded-sm px-5 py-2.5 bg-primary text-primary-foreground text-xs font-semibold tracking-wide hover:bg-white hover:text-black transition-all"
                >
                  <CreditCard className="w-3.5 h-3.5" />
                  {connecting ? 'Setting up...' : 'Connect Stripe Account'}
                </button>
              </div>
            )}
          </div>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { icon: Users, label: 'Clicks', value: stats.total_clicks || 0 },
                { icon: TrendingUp, label: 'Conversions', value: stats.total_conversions || 0 },
                { icon: DollarSign, label: 'Total Earned', value: `$${(stats.total_earnings || 0).toFixed(2)}` },
                { icon: Clock, label: 'Pending', value: `$${(stats.pending_earnings || 0).toFixed(2)}` },
              ].map((s, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-sm p-6" data-testid={`stat-${s.label.toLowerCase()}`}>
                  <s.icon className="w-5 h-5 text-primary mb-2" strokeWidth={1.5} />
                  <p className="font-serif text-2xl text-foreground">{s.value}</p>
                  <p className="text-xs text-muted-foreground">{s.label}</p>
                </div>
              ))}
            </div>
          )}

          {/* Tier info */}
          <div className="bg-white/5 border border-white/10 rounded-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Current Tier</p>
                <p className="font-serif text-xl text-primary mt-1">{stats?.tier || affData.tier || 'Bronze'}</p>
              </div>
              <div className="text-right">
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Commission Rate</p>
                <p className="font-serif text-xl text-foreground mt-1">{((stats?.commission_rate || 0.10) * 100).toFixed(0)}%</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
